'use client';

import { useMemo, useState } from 'react';

type ShiftKey = 'morning' | 'afternoon' | 'night';

type ShiftDefinition = {
  key: ShiftKey;
  label: string;
  shortLabel: string;
  color: string;
  shape: 'circle' | 'diamond' | 'square';
  planned: boolean;
};

type AttendanceRecord = {
  day: number;
  shifts: ShiftKey[];
  leave?: string;
  note?: string;
};

type Employee = {
  id: string;
  name: string;
  role: string;
  team: string;
  records: AttendanceRecord[];
};

const monthStart = new Date(2026, 7, 1);
const totalDays = 30;

const weekdayLabels = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const seedShiftDefinitions: ShiftDefinition[] = [
  { key: 'morning', label: 'Ca sáng', shortLabel: 'Sáng', color: '#38bdf8', shape: 'circle', planned: true },
  { key: 'afternoon', label: 'Ca chiều', shortLabel: 'Chiều', color: '#fb923c', shape: 'diamond', planned: true },
  { key: 'night', label: 'Ca tối', shortLabel: 'Tối', color: '#22c55e', shape: 'square', planned: true },
];

const employeeSeed: Employee[] = [
  {
    id: 'nv-001',
    name: 'Nguyễn An',
    role: 'Kỹ thuật hiện trường',
    team: 'Tổ A',
    records: [],
  },
  {
    id: 'nv-002',
    name: 'Trần Bình',
    role: 'Thu ngân',
    team: 'Tổ B',
    records: [],
  },
  {
    id: 'nv-003',
    name: 'Lê Chi',
    role: 'Giám sát ca',
    team: 'Tổ C',
    records: [],
  },
  {
    id: 'nv-004',
    name: 'Phạm Dũng',
    role: 'Kho vận',
    team: 'Tổ D',
    records: [],
  },
];

function createRecords(
  pattern: ShiftKey[][],
  leaveDays: number[],
  notes: Record<number, string> = {}
): AttendanceRecord[] {
  const leaveSet = new Set(leaveDays);

  return Array.from({ length: totalDays }, (_, index) => {
    const day = index + 1;

    if (leaveSet.has(day)) {
      return {
        day,
        shifts: [],
        leave: day % 2 === 0 ? 'Nghỉ phép' : 'Nghỉ bù',
        note: notes[day],
      };
    }

    const shifts = pattern[index % pattern.length] || [];

    return {
      day,
      shifts,
      note: notes[day],
    };
  });
}

const employees: Employee[] = employeeSeed.map((employee, index) => {
  if (employee.id === 'nv-001') {
    return {
      ...employee,
      records: createRecords(
        [['morning'], ['morning'], ['afternoon'], ['morning', 'afternoon'], ['night'], ['morning'], ['afternoon']],
        [7, 14, 21, 28],
        { 12: 'Đổi ca với chi nhánh 2', 25: 'Chốt sớm để bàn giao' }
      ),
    };
  }

  if (employee.id === 'nv-002') {
    return {
      ...employee,
      records: createRecords(
        [['afternoon'], ['afternoon'], ['morning'], ['night'], ['afternoon'], ['morning', 'afternoon'], ['night']],
        [5, 13, 19, 27],
        { 8: 'Làm thêm giờ', 23: 'Đã duyệt tăng ca' }
      ),
    };
  }

  if (employee.id === 'nv-003') {
    return {
      ...employee,
      records: createRecords(
        [['morning', 'afternoon'], ['morning'], ['night'], ['afternoon'], ['night'], ['morning'], ['afternoon']],
        [6, 17, 24],
        { 10: 'Kiểm tra ca mẫu', 30: 'Chốt bảng công' }
      ),
    };
  }

  return {
    ...employee,
    records: createRecords(
      [['night'], ['night'], ['afternoon'], ['morning'], ['night'], ['morning', 'afternoon'], ['afternoon']],
      [4, 11, 18, 26],
      { 15: 'Hỗ trợ kiểm kê', 29: 'Đổi ca đêm' }
    ),
  };
});

function getCalendarCells() {
  const leadingBlanks = Array.from({ length: (monthStart.getDay() + 6) % 7 }, () => null);

  const days = Array.from({ length: totalDays }, (_, index) => {
    const day = index + 1;
    const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
    return {
      day,
      date,
      weekday: weekdayLabels[(date.getDay() + 6) % 7],
    };
  });

  return [...leadingBlanks, ...days];
}

function shapeStyle(shape: ShiftDefinition['shape'], color: string) {
  const base = {
    width: 14,
    height: 14,
    background: color,
    boxShadow: `0 0 0 2px ${color}22`,
  } as const;

  if (shape === 'circle') {
    return { ...base, borderRadius: '999px' };
  }

  if (shape === 'diamond') {
    return { ...base, transform: 'rotate(45deg)', borderRadius: 3 };
  }

  return { ...base, borderRadius: 3 };
}

export default function AttendancePage() {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(employees[0].id);
  const [shiftDefinitions, setShiftDefinitions] = useState(seedShiftDefinitions);

  const selectedEmployee = employees.find((employee) => employee.id === selectedEmployeeId) ?? employees[0];

  const calendarCells = useMemo(() => getCalendarCells(), []);

  const metrics = useMemo(() => {
    const workingDays = selectedEmployee.records.filter((record) => record.shifts.length > 0).length;
    const leaveDays = selectedEmployee.records.filter((record) => Boolean(record.leave)).length;
    const nightDays = selectedEmployee.records.filter((record) => record.shifts.includes('night')).length;
    const plannedDays = selectedEmployee.records.filter((record) => record.shifts.length > 0).length;

    return { workingDays, leaveDays, nightDays, plannedDays };
  }, [selectedEmployee]);

  const dayByNumber = new Map(selectedEmployee.records.map((record) => [record.day, record]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section
        style={{
          borderRadius: 24,
          padding: 24,
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.96) 45%, rgba(15,118,110,0.88) 100%)',
          color: '#e2e8f0',
          boxShadow: '0 24px 80px rgba(15,23,42,0.18)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at top right, rgba(56,189,248,0.25), transparent 32%), radial-gradient(circle at bottom left, rgba(34,197,94,0.18), transparent 28%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: 'rgba(148,163,184,0.16)',
                  color: '#cbd5e1',
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 999, background: '#38bdf8' }} />
                Bảng chấm công 30 ngày
              </div>
              <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.1 }}>Chọn từng nhân viên và xem lịch ca theo ô</h1>
              <p style={{ margin: '10px 0 0', maxWidth: 820, color: '#cbd5e1', lineHeight: 1.6 }}>
                Mỗi ô là một ngày, màu sắc và hình học phản ánh ca làm việc. Ca sáng, chiều, tối lấy từ danh mục
                ca làm việc riêng và có thể đổi màu trước khi chốt kế hoạch.
              </p>
            </div>

            <div
              style={{
                minWidth: 250,
                padding: 16,
                borderRadius: 18,
                background: 'rgba(15,23,42,0.45)',
                border: '1px solid rgba(148,163,184,0.18)',
                backdropFilter: 'blur(14px)',
              }}
            >
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>Người đang xem</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{selectedEmployee.name}</div>
              <div style={{ marginTop: 6, color: '#cbd5e1', fontSize: 14 }}>
                {selectedEmployee.role} · {selectedEmployee.team}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {employees.map((employee) => {
              const active = employee.id === selectedEmployeeId;
              return (
                <button
                  key={employee.id}
                  onClick={() => setSelectedEmployeeId(employee.id)}
                  style={{
                    border: 'none',
                    cursor: 'pointer',
                    padding: '12px 14px',
                    borderRadius: 16,
                    background: active ? 'rgba(56,189,248,0.16)' : 'rgba(15,23,42,0.35)',
                    color: active ? '#e0f2fe' : '#cbd5e1',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: active ? 'rgba(56,189,248,0.45)' : 'rgba(148,163,184,0.18)',
                    minWidth: 190,
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{employee.name}</div>
                  <div style={{ fontSize: 13, opacity: 0.86 }}>
                    {employee.role} · {employee.team}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
            }}
          >
            {[
              { label: 'Ngày có ca', value: metrics.workingDays, hint: 'Tổng số ngày có ít nhất một ca' },
              { label: 'Ngày nghỉ', value: metrics.leaveDays, hint: 'Nghỉ phép, nghỉ bù, nghỉ theo kế hoạch' },
              { label: 'Ca tối', value: metrics.nightDays, hint: 'Các ngày có ít nhất một ca tối' },
              { label: 'Kế hoạch chốt', value: metrics.plannedDays, hint: 'Dùng để so với lịch đã lên trước' },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: 16,
                  borderRadius: 18,
                  background: 'rgba(15,23,42,0.4)',
                  border: '1px solid rgba(148,163,184,0.18)',
                }}
              >
                <div style={{ fontSize: 13, color: '#94a3b8' }}>{item.label}</div>
                <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800 }}>{item.value}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>{item.hint}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(320px, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <div
          style={{
            borderRadius: 24,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 20px 50px rgba(15,23,42,0.08)',
            padding: 20,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22 }}>Lịch chấm công</h2>
              <p style={{ margin: '6px 0 0', color: '#64748b' }}>30 ngày theo mẫu lịch, thể hiện ca và ngày nghỉ bằng màu sắc khác nhau.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {shiftDefinitions.map((shift) => (
                <div
                  key={shift.key}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 999,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span style={shapeStyle(shift.shape, shift.color)} />
                  <span style={{ fontSize: 13, color: '#334155' }}>{shift.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 10,
              marginTop: 18,
            }}
          >
            {weekdayLabels.map((day) => (
              <div key={day} style={{ textAlign: 'center', color: '#64748b', fontSize: 13, fontWeight: 700 }}>
                {day}
              </div>
            ))}

            {calendarCells.map((cell, index) => {
              if (!cell) {
                return (
                  <div
                    key={`blank-${index}`}
                    style={{
                      minHeight: 130,
                      borderRadius: 18,
                      background: '#f8fafc',
                      border: '1px dashed #e2e8f0',
                    }}
                  />
                );
              }

              const record = dayByNumber.get(cell.day);
              const isLeave = Boolean(record?.leave);

              return (
                <div
                  key={cell.day}
                  style={{
                    minHeight: 130,
                    borderRadius: 18,
                    padding: 12,
                    background: isLeave ? 'linear-gradient(180deg, #fff7ed 0%, #fff 100%)' : '#ffffff',
                    border: isLeave ? '1px solid #fed7aa' : '1px solid #e2e8f0',
                    boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{cell.weekday}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{cell.day}</div>
                    </div>
                    {record?.note ? (
                      <div
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          background: '#e0f2fe',
                          color: '#0369a1',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        Ghi chú
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                    {record?.leave ? (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          alignSelf: 'flex-start',
                          padding: '7px 10px',
                          borderRadius: 999,
                          background: '#fee2e2',
                          color: '#b91c1c',
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: '#ef4444' }} />
                        {record.leave}
                      </div>
                    ) : null}

                    {record?.shifts.length ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {record.shifts.map((shiftKey) => {
                          const shift = shiftDefinitions.find((item) => item.key === shiftKey)!;
                          return (
                            <div
                              key={`${cell.day}-${shiftKey}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 9px',
                                borderRadius: 999,
                                background: `${shift.color}16`,
                                color: '#0f172a',
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              <span style={shapeStyle(shift.shape, shift.color)} />
                              {shift.shortLabel}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      !record?.leave && (
                        <div
                          style={{
                            padding: '7px 10px',
                            borderRadius: 999,
                            background: '#f8fafc',
                            color: '#64748b',
                            fontSize: 12,
                            fontWeight: 600,
                            alignSelf: 'flex-start',
                          }}
                        >
                          Chưa gán ca
                        </div>
                      )
                    )}
                  </div>

                  {record?.note ? (
                    <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                      {record.note}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      {record?.leave ? 'Ngày nghỉ đã ghi nhận trong kế hoạch.' : 'Ca làm việc đã được gán theo danh mục shift.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div
            style={{
              borderRadius: 24,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 50px rgba(15,23,42,0.08)',
              padding: 20,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20 }}>Ca làm việc</h2>
            <p style={{ margin: '6px 0 16px', color: '#64748b', lineHeight: 1.5 }}>
              Màu sắc của từng ca có thể chỉnh trước khi chốt bảng công. Khi đồng bộ với dữ liệu thật, phần này sẽ
              lấy từ bảng định nghĩa ca làm việc.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {shiftDefinitions.map((shift, index) => (
                <div
                  key={shift.key}
                  style={{
                    borderRadius: 18,
                    padding: 14,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={shapeStyle(shift.shape, shift.color)} />
                      <div>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{shift.label}</div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {shift.planned ? 'Đã lên kế hoạch' : 'Chưa chốt kế hoạch'}
                        </div>
                      </div>
                    </div>
                    <label
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 12,
                        overflow: 'hidden',
                        border: '1px solid #cbd5e1',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                      }}
                      title={`Đổi màu ${shift.label}`}
                    >
                      <input
                        type="color"
                        value={shift.color}
                        onChange={(event) => {
                          const nextColor = event.target.value;
                          setShiftDefinitions((current) =>
                            current.map((item, currentIndex) =>
                              currentIndex === index ? { ...item, color: nextColor } : item
                            )
                          );
                        }}
                        style={{
                          width: 50,
                          height: 50,
                          border: 'none',
                          padding: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                        }}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 10px',
                        borderRadius: 999,
                        background: `${shift.color}16`,
                        color: '#0f172a',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <span style={shapeStyle(shift.shape, shift.color)} />
                      Hình {shift.shape}
                    </span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Màu được dùng trong lịch 30 ngày</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              borderRadius: 24,
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 20,
              boxShadow: '0 20px 50px rgba(15,23,42,0.16)',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 18 }}>Hướng triển khai dữ liệu thật</h3>
            <ul style={{ margin: '14px 0 0', paddingLeft: 18, lineHeight: 1.7, color: '#cbd5e1' }}>
              <li>Ca làm việc lưu trong bảng riêng, có màu và hình học mặc định.</li>
              <li>Bảng công 30 ngày đọc theo nhân viên được chọn.</li>
              <li>Ngày nghỉ, đổi ca, tăng ca và ghi chú tách thành trạng thái riêng.</li>
              <li>Khi cần, có thể chốt lịch trước rồi đối chiếu với công thực tế.</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
